import { LocalOverrideAdapter } from '@statsig/js-local-overrides';
import {
  StatsigClient,
  StatsigProvider,
  useClientAsyncInit,
} from '@statsig/react-bindings';
import CenteredProgress from 'components/progress/centered-progress/centered-progress.component';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectStatsigUser } from 'redux/user/user.selectors';
import { getEnv } from 'util/getEnv';
import { useUpdateStatsigUser } from './statsig-wrapper.hooks';

const StatsigWrapper = ({ children }) => {
  const isTestENV = getEnv('NODE_ENV') === 'test';

  /* istanbul ignore next */
  if (isTestENV) {
    return <TestStatsigWrapper>{children}</TestStatsigWrapper>;
  }

  /* istanbul ignore next */
  return <RealStatsigWrapper>{children}</RealStatsigWrapper>;
};

/**
 * Dev-only escape hatch: mirror `TestStatsigWrapper` and apply local gate
 * overrides so a local session can force gates on without touching the Statsig
 * console. In `REACT_APP_MOCK_API` mode the Data Plus gate is forced on by
 * default (so the Benchmarks / Retention tabs render against mock data instead
 * of the paywall); any `window.statsigOverrides.gates` set by the caller take
 * precedence. Returns `undefined` outside development so deployed environments
 * always evaluate real gate values.
 */
/* istanbul ignore next */
const buildDevOverrideAdapter = () => {
  if (getEnv('REACT_APP_ENV') !== 'development') return undefined;

  const gates = {
    ...(getEnv('REACT_APP_MOCK_API') === 'true'
      ? { 'guest-journey-data-plus': true }
      : {}),
    ...window.statsigOverrides?.gates,
  };
  if (!Object.keys(gates).length) return undefined;

  const overrideAdapter = new LocalOverrideAdapter();
  Object.entries(gates).forEach(([gateName, value]) => {
    overrideAdapter.overrideGate(gateName, value);
  });
  return overrideAdapter;
};

/* istanbul ignore next */
const RealStatsigWrapper = ({ children }) => {
  const user = useSelector(selectStatsigUser);
  const [overrideAdapter] = useState(buildDevOverrideAdapter);
  const { client } = useClientAsyncInit(
    getEnv('REACT_APP_STATSIG_CLIENT_SDK_KEY'),
    user,
    {
      environment: { tier: getEnv('REACT_APP_ENV') },
      ...(overrideAdapter ? { overrideAdapter } : {}),
    },
  );
  const { userLoading } = useUpdateStatsigUser(client, user);

  if (userLoading) {
    return <CenteredProgress />;
  }

  return (
    <StatsigProvider client={client} loadingComponent={<CenteredProgress />}>
      {children}
    </StatsigProvider>
  );
};

const TestStatsigWrapper = ({ children }) => {
  const user = useSelector(selectStatsigUser);
  const [client] = useState(() => {
    const overrideAdapter = new LocalOverrideAdapter();
    if (window.statsigOverrides?.gates) {
      Object.entries(window.statsigOverrides.gates).forEach(
        ([gateName, value]) => {
          overrideAdapter.overrideGate(gateName, value);
        },
      );
    }

    const client = new StatsigClient('client-test', user, {
      environment: { tier: 'development' },
      overrideAdapter,
      networkConfig: {
        preventAllNetworkTraffic: true,
      },
    });
    client.initializeAsync();
    return client;
  });

  const { userLoading } = useUpdateStatsigUser(client, user);

  /* istanbul ignore next */
  if (userLoading) {
    /* istanbul ignore next */
    return <CenteredProgress />;
  }

  return (
    <StatsigProvider client={client} loadingComponent={<CenteredProgress />}>
      {children}
    </StatsigProvider>
  );
};

export default StatsigWrapper;
